import React, { useMemo, useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import { ViewModelForm, useDataRefresh} from '@xengage/gw-portals-viewmodel-react';
import { MLModalService } from 'ml-portals-services-react';
import { useTranslator } from '@jutro/locale';
import moment from 'moment';
import PropTypes from 'prop-types';
import { DateUtil } from '@xengage/gw-portals-util-js';
import { useModal } from '@jutro/components';
import {DocumentDownloadService } from '@xengage/gw-portals-document-js';
import { FormModalPopover } from 'ml-components-platform-react';
import { MLNewDocumentsCounterService } from 'ml-services-common-react'

import { getProxiedServiceUrl } from '@xengage/gw-portals-url-js';
import MLDocumentsUploadPopup from '../../../gw-capability-gateway-react/Policies/MLDocumentsUploadPopup/MLDocumentsUploadPopup.jsx';

import styles from './DocumentsTableComponent.module.scss';
import messages from './DocumentsTableComponent.messages';
import metadata from './DocumentsTableComponent.metadata.json5';

const TIMEFRAME_30_DAYS = '30days';
const TIMEFRAME_90_DAYS = '90days';
const TIMEFRAME_6_MONTHS = '6months';
const TIMEFRAME_1_YEAR = '1year';
const TIMEFRAME_3_YEARS = '3years';
const TIMEFRAME_5_YEARS = '5years';
const DOCUMENT_READY_STATUS = 'final';

function DocumentsTableComponent(props) {
    const modalApi = useModal();
    const { summary, authHeader, formContingencies, policySummaries, onCloseUploadModal, viewModelService } = props
    const [ documentData, setDocumentData ] = useState([]);
    const { refreshData } = useDataRefresh();
    const [ dayFilter, setDayFilter] = useState(TIMEFRAME_90_DAYS);
    const { showErrorModal, showProgressDialog} = MLModalService();
    const [ currentView, setCurrentView] = useState();
    const translator = useTranslator();

    const addDays = (date, daysToAdd) => {
        date.setDate(date.getDate() + daysToAdd);
        return date;
    }

    const validateLineOfBusiness = (policy, policyLine) =>{
        return policy 
            && policy.lines 
            && policy.lines.some((line) => line === policyLine); 
    };

    const isHOPLine = (policy) => validateLineOfBusiness(policy, 'HOPLine');

    const isPALine = (policy) => validateLineOfBusiness(policy, 'PersonalAutoLine');

    const isPUPLine = (policy) => validateLineOfBusiness(policy, 'PUPLine_PUE');

    const documentViewDropdownValue = useMemo(() => {
        return isPALine(summary) ? [
            {
                "code": "allDocuments",
                "name": translator(messages.allDocuments)
            },
            {
                "code": "idCards",
                "name": translator(messages.idCards)
            },
            {
                "code": "policyDocuments",
                "name": translator(messages.policyDocuments)
            },
            {
                "code": "billingDocuments",
                "name": translator(messages.billingDocuments)

            },
            {
                "code": "claimsDocuments",
                "name": translator(messages.claimsDocuments)

            },
            {
                "code": "sentDocuments",
                "name": translator(messages.documentsYouSent)

            }
        ] : [
            {
                "code": "allDocuments",
                "name": translator(messages.allDocuments)
            },
            {
                "code": "policyDocuments",
                "name": translator(messages.policyDocuments)
            },
            {
                "code": "billingDocuments",
                "name": translator(messages.billingDocuments)

            },
            {
                "code": "claimsDocuments",
                "name": translator(messages.claimsDocuments)

            },
            {
                "code": "sentDocuments",
                "name": translator(messages.documentsYouSent)

            }
        ]}, [isPALine, summary, translator])

    const isCurrentView = (docs, view) => {
        return docs.currentView === view;
    };

    const changeCurrentView = useCallback((docs, selectedView) => {
        setCurrentView(selectedView);
        docs.currentView = selectedView;
        refreshData();
    }, [refreshData]);

    const shouldShowHomeInspection = (period) => {
        if (!isHOPLine(period)) {
            return;
        }
        return period.details && period.details.hasBoundHomeInspection;
    };

    const setFilterDate = useCallback((option) => {
        let filterDate;
        const currentDate = DateUtil.currentDate();
        if (option === TIMEFRAME_30_DAYS) {
            filterDate = addDays(currentDate, -30);
        } else if (option === TIMEFRAME_90_DAYS) {
            filterDate = addDays(currentDate, -90);
        } else if (option === TIMEFRAME_6_MONTHS) {
            filterDate = new Date(currentDate.setMonth(currentDate.getMonth() - 6));
        } else if (option === TIMEFRAME_1_YEAR) {
            filterDate = new Date(currentDate.setFullYear(currentDate.getFullYear() - 1));
        } else if (option === TIMEFRAME_3_YEARS) {
            filterDate = new Date(currentDate.setFullYear(currentDate.getFullYear() - 3));
        } else if (option === TIMEFRAME_5_YEARS) {
            filterDate = new Date(currentDate.setFullYear(currentDate.getFullYear() - 5));
        }
        return filterDate;
    },[]);

    const filterDocumentsByDate = (docs, filterDate) => {
        if (!docs) {
            return [];
        }
        if (!filterDate) {
            return docs;
        }
        return docs.filter((doc) => {
            const dateFilter = new Date(doc.dateModified)
            return dateFilter >= filterDate;
        });
    };

    const documentReady = (document) => {
        return document.status === DOCUMENT_READY_STATUS;
    };

    const formatDateCell = (rowData) => {
        const dateFormat = 'MM/DD/YYYY'
        return _.isEmpty(rowData.dateModified) ? '' : moment(rowData.dateModified).format(dateFormat);
    };

    const openDocument = useCallback((item) => {
        const publicID = item.publicID;
        const sessionID = item.sessionID;
        let xCenterRetrievalURI = null;
        const xCenter = item.xcenter || item.xcenter_ML;
        switch (xCenter) {
            case 'cc':
                xCenterRetrievalURI = getProxiedServiceUrl('claimDocument');
                break;
            case 'bc':
                xCenterRetrievalURI = getProxiedServiceUrl('billingDocument');
                break;
            case 'pc':
            default:
                xCenterRetrievalURI = getProxiedServiceUrl('policyDocument');
        };

        const displayErrorModal = () => {
            showErrorModal(
                translator(messages.documentDownloadErrorHeader),
                translator(messages.documentDownloadErrorMessage)
            )};
        
        const link = DocumentDownloadService.getDocumentLink(xCenterRetrievalURI, publicID, sessionID);
        const httpPromise = DocumentDownloadService.getDocument(link, authHeader, displayErrorModal)
        httpPromise.then(() => {
            if (item.newToPortal || item.newToPortal_ML) {
                MLNewDocumentsCounterService.setDocumentAsViewed(xCenter, publicID, sessionID, item, authHeader);
            }
            item.isActionRequired_ML = false;
            refreshData()
        })
        showProgressDialog(translator(messages.downloadingDocument), httpPromise);
    },[authHeader, showErrorModal, showProgressDialog, translator, refreshData]);

    const getDocumentsData = useCallback(() => {
        let docsData;
        switch (summary.documents.currentView) { 
            case 'allDocuments':
                docsData = summary.documents.filteredAllDocuments;
                break;
            case 'idCards':
                docsData = summary.documents.filteredIdCards;
                break;
            case 'policyDocuments':
                docsData = summary.documents.filteredPolicyDocuments;
                break;
            case 'billingDocuments':
                docsData = summary.documents.filteredBillingDocuments;
                break;
            case 'claimsDocuments':
                docsData = summary.documents.claimsDocs;
                break;
            case 'sentDocuments':
                docsData = summary.documents.filteredDocsYouSentUs;
                break;
            default:
                break;
        }
        return docsData;
    },[])

    const updateFilteredDocsByDate = useCallback((selectedValue, docs) => {
        docs.selectedTimeframeOption = selectedValue;
        const filterDate = setFilterDate(docs.selectedTimeframeOption);
        docs.filteredAllDocuments = filterDocumentsByDate(docs.allDocuments, filterDate);
        docs.filteredPolicyDocuments = filterDocumentsByDate(docs.policyDocs, filterDate);
        docs.filteredBillingDocuments = filterDocumentsByDate(docs.billingDocs, filterDate);
        docs.filteredDocsYouSentUs = filterDocumentsByDate(docs.docsYouSentUs, filterDate);
        docs.filteredIdCards = filterDocumentsByDate(docs.idCards, filterDate);
        setDayFilter(selectedValue);
        setDocumentData(getDocumentsData())
    },[setFilterDate]);

    useEffect(()=> {
        setDocumentData(getDocumentsData())
    }, [currentView])

    const onUploadDocument = () => {
        const componentProps = {
            showHeader: false,
            overrideProps: {
                policyNumber: summary.policyId,
                policies: policySummaries,
                formContingencies: formContingencies
            },
            viewModelService,
            authHeader,
            CustomComponent: MLDocumentsUploadPopup
        };

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).catch(() => {
            onCloseUploadModal()
        });
    };

    const documentExist = (documents) => {
        return (documents && documents.length > 0);
    }

    const generateOverrides = () => {
        
        const documentsOverrides = _.orderBy(documentData, 'dateModified', 'desc').map((doc, index) => {
            return {
                [`documentDate${index}`]: {
                    content: formatDateCell(doc)
                },
                [`documentName${index}`]:{
                    className: (doc.newToPortal_ML || doc.newToPortal) ? 'font-weight-bold' : ''
                },
                [`documentDataNameReady${index}`]: {
                    visible: documentReady(doc),
                    content: doc.name,
                    onClick: () => openDocument(doc)
                },
                [`documentDataNameNotReady${index}`]: {
                    visible: !documentReady(doc),
                    content: doc.name
                },
                [`documentNewToPortal${index}`]:{
                    visible: !!(doc.newToPortal_ML || doc.newToPortal)
                },
                [`documentActionRequired${index}`]:{
                    visible: doc.isActionRequired_ML
                },
                [`documentsType${index}`]:{
                    content: _.get(doc, 'mimeTypeDescription_ML')
                },
                [`actionWithLinkContainer${index}`]:{
                    visible: documentReady(doc)
                },
                [`actionWithLink${index}`]:{
                    onClick: () => openDocument(doc)
                },
                [`actionNoLink${index}`]: {
                    visible: !documentReady(doc)
                }
            }
        })
        return Object.assign({}, ...documentsOverrides);
    };

    const overrideProps = {
        DocumentsUploadButton: {
            onClick: () => onUploadDocument()
        },
        DocumentsListingTable: {
            visible: documentExist(documentData),
        },
        documentsIterable: {
            data: documentData
        },
        DocumentNotExistContainer: {
            visible: !documentExist(documentData)
        },
        DocumentDaysDropdown: {
            value: dayFilter,
            onValueChange: (value) => updateFilteredDocsByDate(value, summary.documents)
        },
        DocumentsFilterDropdown: {
            availableValues: documentViewDropdownValue,
            value: summary.documents.currentView,
            onValueChange: (value) => changeCurrentView(summary.documents, value)
        },
        DocumentsAllFilterButton: {
            onClick: () => changeCurrentView(summary.documents, 'allDocuments'),
            className: isCurrentView(summary.documents, 'allDocuments') ? 'ml-btn ml-btn-default documents-custom-btn active buttonText' : 
                'ml-btn ml-btn-default documents-custom-btn notActive buttonText' 
        },
        DocumentsIdCardsFilterButton: {
            visible: isPALine(summary),
            onClick: () => changeCurrentView(summary.documents, 'idCards'),
            className: isCurrentView(summary.documents, 'idCards') ? 'ml-btn ml-btn-default documents-custom-btn active buttonText' : 
                'ml-btn ml-btn-default documents-custom-btn notActive buttonText' 
        },
        DocumentsPolicyFilterButton: {
            onClick: () => changeCurrentView(summary.documents, 'policyDocuments'),
            className: isCurrentView(summary.documents, 'policyDocuments') ? 'ml-btn ml-btn-default documents-custom-btn active buttonText' : 
                'ml-btn ml-btn-default documents-custom-btn notActive buttonText' 
        },
        DocumentsBillingFilterButton: {
            onClick: () => changeCurrentView(summary.documents, 'billingDocuments'),
            className: isCurrentView(summary.documents, 'billingDocuments') ? 'ml-btn ml-btn-default documents-custom-btn active buttonText' : 
                'ml-btn ml-btn-default documents-custom-btn notActive buttonText' 
        },
        DocumentsClaimsFilterButton: {
            onClick: () => changeCurrentView(summary.documents, 'claimsDocuments'),
            className: isCurrentView(summary.documents, 'claimsDocuments') ? 'ml-btn ml-btn-default documents-custom-btn active buttonText' : 
                'ml-btn ml-btn-default documents-custom-btn notActive buttonText' 
        },
        DocumentsYouSentFilterButton: {
            onClick: () => changeCurrentView(summary.documents, 'sentDocuments'),
            className: isCurrentView(summary.documents, 'sentDocuments') ? 'ml-btn ml-btn-default documents-custom-btn active buttonText' : 
                'ml-btn ml-btn-default documents-custom-btn notActive buttonText' 
        },
        ...generateOverrides()
    }
    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap:{

        },
        resolveCallbackMap: {
        }
    }

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

DocumentsTableComponent.propTypes = {
    summary: PropTypes.object.isRequired,
    authHeader: PropTypes.string.isRequired
}

export default DocumentsTableComponent;